import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { resolveProfileMediaUrl } from '@/shared/lib/resolveProfileMediaUrl'
import { adminDelete, adminFetch, clearAdminToken, getAdminToken } from './adminApi'

type AdminUserRow = {
  id: string
  maxUserId: string
  createdAt: string
  lastActiveAt: string
  isBanned: boolean
  phone: string | null
  isPhoneVerified: boolean
  maxAvatarUrl: string | null
  maxUsername: string | null
  maxFullName: string | null
  isPremium: boolean
  isVideoVerified: boolean
  name: string | null
  city: string | null
  gender: string | null
  goals: string[]
  birthDate: string | null
  bio: string | null
  heightCm: number | null
  education: string | null
  occupation: string | null
  smoking: string | null
  alcohol: string | null
  kids: string | null
  searchGender: string | null
  searchAgeFrom: number | null
  searchAgeTo: number | null
  searchRadius: number | null
  eloScore: number | null
  isHidden: boolean | null
  moderationStatus: string | null
  profileUpdatedAt: string | null
  cityId: number | null
  geoLat: number | null
  geoLng: number | null
  /** Все фото анкеты (APPROVED + PENDING), порядок как в профиле. */
  photoGallery?: Array<{ url: string; thumbUrl: string }>
  /** Превью URL (дубль из галереи). */
  photoThumbUrls?: string[]
  likesGiven: number
  likesReceived: number
  skipsGiven: number
  matchesTotal: number
  messagesSent: number
}

type UsersResponse = {
  total: number
  offset: number
  limit: number
  users: AdminUserRow[]
}

const PAGE_SIZE = 100

const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Сначала новые (регистрация)' },
  { value: 'created_asc', label: 'Сначала старые (регистрация)' },
  { value: 'active_desc', label: 'По последней активности' },
  { value: 'name_asc', label: 'По имени А→Я' },
] as const

function goalRu(g: string) {
  const m: Record<string, string> = {
    SERIOUS: 'Серьёзные',
    FRIENDSHIP: 'Дружба',
    CHAT: 'Общение',
    CASUAL: 'Кэжуал',
    UNDECIDED: 'Не определился',
  }
  return m[g] ?? g
}

function ageFromBirth(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const today = new Date()
  let age = today.getFullYear() - d.getFullYear()
  const m = today.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--
  return String(age)
}

/** Превью в таблице: MAX-аватар + все снимки анкеты (превью, ссылка — полный url). */
function photoCells(u: AdminUserRow): Array<{ href: string; img: string }> {
  const out: Array<{ href: string; img: string }> = []
  const seenImg = new Set<string>()
  const add = (href: string, img: string) => {
    const h = (href || img).trim()
    const i = (img || href).trim()
    if (!h || seenImg.has(i)) return
    seenImg.add(i)
    out.push({ href: resolveProfileMediaUrl(h), img: resolveProfileMediaUrl(i) })
  }
  if (u.maxAvatarUrl?.trim()) {
    add(u.maxAvatarUrl.trim(), u.maxAvatarUrl.trim())
  }
  for (const p of u.photoGallery ?? []) {
    const href = (p.url || p.thumbUrl).trim()
    const img = (p.thumbUrl || p.url).trim()
    if (href || img) add(href || img, img || href)
  }
  if (!(u.photoGallery && u.photoGallery.length > 0) && u.photoThumbUrls?.length) {
    for (const t of u.photoThumbUrls) {
      const s = t.trim()
      if (s) add(s, s)
    }
  }
  return out
}

function tdText(s: string | null | undefined, opts?: { mono?: boolean }) {
  const v = s == null || s === '' ? '—' : String(s)
  return (
    <td
      className={`border-b border-slate-800/80 px-2 py-1.5 align-top text-[11px] leading-snug text-slate-300 ${
        opts?.mono ? 'font-mono' : 'whitespace-normal break-words'
      }`}
    >
      {v}
    </td>
  )
}

export function AdminUsers() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [rows, setRows] = useState<AdminUserRow[]>([])
  const [total, setTotal] = useState(0)
  const [respOffset, setRespOffset] = useState(0)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<string>('created_desc')
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [phoneVerifiedOnly, setPhoneVerifiedOnly] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchDraft.trim()), 400)
    return () => window.clearTimeout(t)
  }, [searchDraft])

  useEffect(() => {
    setPage(1)
  }, [search, sort, phoneVerifiedOnly])

  const maxPage = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage)
    }
  }, [page, maxPage])

  const load = useCallback(async () => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true })
      return
    }
    setLoading(true)
    setErr(null)
    const offset = (page - 1) * PAGE_SIZE
    try {
      const data = await adminFetch<UsersResponse>('/api/admin/stats/users-all', {
        offset: String(offset),
        limit: String(PAGE_SIZE),
        sort,
        search: search || undefined,
        phoneVerified: phoneVerifiedOnly ? '1' : undefined,
      })
      setTotal(data.total)
      setRespOffset(data.offset)
      setRows(data.users)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      if ((e as { status?: number }).status === 401) {
        clearAdminToken()
        navigate('/admin/login', { replace: true })
      }
    } finally {
      setLoading(false)
    }
  }, [navigate, page, sort, search, phoneVerifiedOnly])

  useEffect(() => {
    void load()
  }, [load])

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0'
    const from = respOffset + 1
    const to = respOffset + rows.length
    return `${from}–${to}`
  }, [respOffset, rows.length, total])

  const handleDelete = async (u: AdminUserRow) => {
    const ok = window.confirm(
      `Удалить пользователя из приложения (мягко)?\n\n${u.name ?? 'Без имени'} · ${u.id}\n\nЗапись останется в БД с пометкой deletedAt — из ленты и списков пропадёт.`,
    )
    if (!ok) return
    setDeletingId(u.id)
    setErr(null)
    try {
      await adminDelete<{ ok?: boolean }>(`/api/admin/users/${u.id}`)
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setDeletingId(null)
    }
  }

  const th = (label: string, title?: string) => (
    <th
      className="border-b border-slate-800 bg-slate-900 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500 whitespace-normal"
      title={title}
    >
      {label}
    </th>
  )

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/80 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-[1800px] flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-white">Пользователи</h1>
            <p className="text-xs text-slate-500">
              Всего: {total} · на экране: {rangeLabel} · по {PAGE_SIZE} на страницу · таблица с горизонтальным скроллом
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/dashboard"
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
            >
              ← Аналитика
            </Link>
            <Link to="/dashboard" className="text-sm text-indigo-400 hover:underline">
              В приложение
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1800px] space-y-4 p-4">
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <label className="min-w-[12rem] flex-1 text-xs text-slate-400">
            Поиск (имя, город, био, телефон, UUID)
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-600"
              placeholder="Подстрока…"
              type="search"
            />
          </label>
          <label className="text-xs text-slate-400">
            Сортировка
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="mt-1 block w-full min-w-[14rem] rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={phoneVerifiedOnly}
              onChange={(e) => setPhoneVerifiedOnly(e.target.checked)}
              className="rounded border-slate-600"
            />
            Только вериф. телефон
          </label>
        </div>

        {err ? (
          <div className="rounded-lg border border-red-900/60 bg-red-950/40 p-4 text-sm text-red-200">{err}</div>
        ) : null}

        {loading && rows.length === 0 ? (
          <p className="text-slate-400">Загрузка…</p>
        ) : (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40">
            <div className="max-h-[min(74vh,820px)] overflow-auto">
              <table className="w-max min-w-full border-collapse text-left text-xs">
                <thead className="sticky top-0 z-[1] shadow-sm shadow-slate-950/40">
                  <tr>
                    {th('Фото')}
                    {th('Регистрация')}
                    {th('Активность')}
                    {th('Имя', 'Имя в анкете')}
                    {th('Город')}
                    {th('Возраст', 'Из даты рождения')}
                    {th('Пол')}
                    {th('Био')}
                    {th('Рост')}
                    {th('Образование')}
                    {th('Работа')}
                    {th('Курение')}
                    {th('Алкоголь')}
                    {th('Дети')}
                    {th('Поиск: пол')}
                    {th('Поиск: возраст от')}
                    {th('Поиск: возраст до')}
                    {th('Поиск: км')}
                    {th('ELO')}
                    {th('Скрыта')}
                    {th('Мод. анкеты', 'Статус модерации профиля в БД')}
                    {th('Цели')}
                    {th('cityId')}
                    {th('Шир.', 'geoLat')}
                    {th('Долг.', 'geoLng')}
                    {th('MAX @')}
                    {th('MAX имя')}
                    {th('Прем')}
                    {th('Видео')}
                    {th('Телефон')}
                    {th('Л→', 'Лайки и суперлайки от пользователя')}
                    {th('Л←', 'Лайки и суперлайки на пользователя')}
                    {th('Пр', 'Пропуски')}
                    {th('М', 'Мэтчи')}
                    {th('Со', 'Сообщения')}
                    {th('MAX id')}
                    {th('UUID')}
                    {th('Профиль обн.', 'updatedAt профиля')}
                    {th('')}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => {
                    const pics = photoCells(u)
                    return (
                      <tr
                        key={u.id}
                        className={`border-b border-slate-800/80 hover:bg-slate-800/30 ${u.isBanned ? 'bg-red-950/20' : ''}`}
                      >
                        <td className="border-b border-slate-800/80 px-2 py-2 align-top">
                          <div className="flex max-w-[120px] flex-wrap gap-0.5">
                            {pics.length === 0 ? (
                              <span className="text-slate-600">—</span>
                            ) : (
                              pics.map((cell, i) => (
                                <a
                                  key={`${u.id}-${i}`}
                                  href={cell.href}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block shrink-0"
                                  title="Открыть фото"
                                >
                                  <img
                                    src={cell.img}
                                    alt=""
                                    className="h-10 w-10 rounded object-cover ring-1 ring-slate-600 hover:opacity-90"
                                  />
                                </a>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="border-b border-slate-800/80 whitespace-nowrap px-2 py-1.5 align-top font-mono text-[10px] text-slate-300">
                          {new Date(u.createdAt).toLocaleString('ru-RU', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td className="border-b border-slate-800/80 whitespace-nowrap px-2 py-1.5 align-top font-mono text-[10px] text-slate-400">
                          {new Date(u.lastActiveAt).toLocaleString('ru-RU', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td className="border-b border-slate-800/80 min-w-[9rem] max-w-[14rem] whitespace-normal break-words px-2 py-1.5 align-top text-slate-100">
                          {u.name ?? '—'}
                          {u.isBanned ? (
                            <span className="ml-1 rounded bg-red-900/80 px-1 text-[10px] text-red-200">бан</span>
                          ) : null}
                        </td>
                        <td className="border-b border-slate-800/80 min-w-[6rem] max-w-[10rem] whitespace-normal break-words px-2 py-1.5 align-top text-slate-200">
                          {u.city ?? '—'}
                        </td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 align-top text-slate-300">{ageFromBirth(u.birthDate)}</td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 align-top text-slate-400">
                          {u.gender === 'MALE' ? 'М' : u.gender === 'FEMALE' ? 'Ж' : '—'}
                        </td>
                        <td className="border-b border-slate-800/80 min-w-[10rem] max-w-[18rem] whitespace-normal break-words px-2 py-1.5 align-top text-[11px] text-slate-400">
                          {u.bio ?? '—'}
                        </td>
                        {tdText(u.heightCm != null ? String(u.heightCm) : null)}
                        {tdText(u.education)}
                        {tdText(u.occupation)}
                        {tdText(u.smoking)}
                        {tdText(u.alcohol)}
                        {tdText(u.kids)}
                        {tdText(u.searchGender)}
                        {tdText(u.searchAgeFrom != null ? String(u.searchAgeFrom) : null)}
                        {tdText(u.searchAgeTo != null ? String(u.searchAgeTo) : null)}
                        {tdText(u.searchRadius != null ? String(u.searchRadius) : null)}
                        {tdText(u.eloScore != null ? String(u.eloScore) : null)}
                        <td className="border-b border-slate-800/80 px-2 py-1.5 align-top text-slate-400">
                          {u.isHidden == null ? '—' : u.isHidden ? 'да' : 'нет'}
                        </td>
                        {tdText(u.moderationStatus)}
                        <td className="border-b border-slate-800/80 min-w-[7rem] max-w-[12rem] whitespace-normal break-words px-2 py-1.5 align-top text-[10px] text-slate-400">
                          {u.goals.length ? u.goals.map(goalRu).join(', ') : '—'}
                        </td>
                        {tdText(u.cityId != null ? String(u.cityId) : null, { mono: true })}
                        {tdText(u.geoLat != null ? String(u.geoLat) : null, { mono: true })}
                        {tdText(u.geoLng != null ? String(u.geoLng) : null, { mono: true })}
                        {tdText(u.maxUsername)}
                        {tdText(u.maxFullName)}
                        <td className="border-b border-slate-800/80 px-2 py-1.5 align-top text-slate-400">{u.isPremium ? 'да' : 'нет'}</td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 align-top text-slate-400">{u.isVideoVerified ? 'да' : 'нет'}</td>
                        <td className="border-b border-slate-800/80 max-w-[10rem] break-all px-2 py-1.5 align-top font-mono text-[10px] text-slate-300">
                          {u.phone ? (
                            <>
                              <span className="text-slate-500">{u.isPhoneVerified ? '✓ ' : '○ '}</span>
                              {u.phone}
                            </>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 text-right align-top font-mono text-slate-200">{u.likesGiven}</td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 text-right align-top font-mono text-slate-200">{u.likesReceived}</td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 text-right align-top font-mono text-slate-200">{u.skipsGiven}</td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 text-right align-top font-mono text-slate-200">{u.matchesTotal}</td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 text-right align-top font-mono text-slate-200">{u.messagesSent}</td>
                        <td className="border-b border-slate-800/80 max-w-[8rem] break-all px-2 py-1.5 align-top font-mono text-[10px] text-slate-500">
                          {u.maxUserId}
                        </td>
                        <td className="border-b border-slate-800/80 min-w-[16rem] max-w-[22rem] break-all px-2 py-1.5 align-top font-mono text-[11px] leading-snug text-slate-400">
                          {u.id}
                        </td>
                        <td className="border-b border-slate-800/80 whitespace-nowrap px-2 py-1.5 align-top font-mono text-[10px] text-slate-500">
                          {u.profileUpdatedAt
                            ? new Date(u.profileUpdatedAt).toLocaleString('ru-RU', {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })
                            : '—'}
                        </td>
                        <td className="border-b border-slate-800/80 px-2 py-1.5 text-center align-top">
                          <button
                            type="button"
                            disabled={deletingId === u.id}
                            onClick={() => void handleDelete(u)}
                            className="rounded-md border border-red-900/60 bg-red-950/50 px-2 py-1 text-[10px] font-semibold text-red-200 hover:bg-red-900/60 disabled:opacity-40"
                            title="Мягкое удаление"
                          >
                            {deletingId === u.id ? '…' : 'Удал.'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-xl text-xs text-slate-500">
            Колонка «мод. анкеты» — технический статус в БД (часто APPROVED для тестовых анкет). Имя и город в отдельных колонках с переносами строк.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={loading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40"
            >
              Назад
            </button>
            <span className="text-sm text-slate-400">
              Стр. {page} / {maxPage}
            </span>
            <button
              type="button"
              disabled={loading || page >= maxPage}
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40"
            >
              Вперёд
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
